import { PlanMetadataModel } from '@gfe/core/models/plan.model';

import { isNil } from './shared-utils';

/**
 * The plans a delete of `planIds` must cover: the selection plus every plan below it in
 * the optimization tree.
 *
 * The batch delete route refuses the whole call unless every child of a deleted plan is
 * in the same request, so the selection is expanded here rather than letting the user
 * discover that rule through a 400.
 *
 * Children are read from `parentPlanId`, not `childrenInfo`: the list response always
 * carries the former (the graph and the Parent column are built from it), while
 * `childrenInfo` is optional, may omit `childPlanId`, and omits jobs whose plan has been
 * deleted. Every child counts regardless of how it was created, duplicates included.
 *
 * Ids absent from `plans` are dropped: a selection made stale by a concurrent delete
 * would otherwise put an unknown id in the payload and earn a 404.
 */
export function expandToDeletionSet<T extends PlanMetadataModel>(
  plans: readonly T[],
  planIds: readonly string[]
): T[] {
  const childrenByParent = new Map<string, T[]>();
  for (const plan of plans) {
    if (isNil(plan.parentPlanId)) {
      continue;
    }
    const siblings = childrenByParent.get(plan.parentPlanId) ?? [];
    siblings.push(plan);
    childrenByParent.set(plan.parentPlanId, siblings);
  }

  const planById = new Map(plans.map((plan) => [plan.id, plan]));
  const collected = new Map<string, T>();
  const queue = planIds.filter((id) => planById.has(id));

  while (queue.length > 0) {
    const id = queue.shift() as string;
    // Also what breaks a cycle, should the backend ever report one.
    if (collected.has(id)) {
      continue;
    }
    collected.set(id, planById.get(id) as T);
    queue.push(...(childrenByParent.get(id) ?? []).map((child) => child.id));
  }

  return [...collected.values()];
}
